import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError("Missing session_id in callback URL.");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);
    // Emergent's redirect drops all query-string params but preserves hash,
    // so we stash the "link" intent in sessionStorage before redirecting
    // and pick it back up here.
    const isLink = (typeof window !== "undefined" && window.sessionStorage.getItem("pandax_google_intent") === "link");
    if (typeof window !== "undefined") window.sessionStorage.removeItem("pandax_google_intent");

    (async () => {
      try {
        const endpoint = isLink ? "/auth/link-google" : "/auth/session";
        // Referral attribution survives the OAuth redirect via sessionStorage
        // (Emergent's redirect drops query params). Sent once, then cleared.
        let refHandle = null, refCode = null;
        try {
          refHandle = window.sessionStorage.getItem("pandax_ref_handle");
          refCode = window.sessionStorage.getItem("panda_ref");
          window.sessionStorage.removeItem("pandax_ref_handle");
        } catch (err) { console.debug("session-storage unavailable:", err?.name); }
        const body = isLink ? null : { ref: refHandle || undefined, ref_code: refCode || undefined };
        await api.post(endpoint, body, {
          headers: { "X-Session-Id": sessionId },
        });
        await refresh();
        if (isLink) {
          toast.success("Google account linked.");
          navigate("/feed", { replace: true });
        } else {
          toast.success("Welcome to the vault.");
          navigate("/feed", { replace: true });
        }
      } catch (e) {
        setError(e?.response?.data?.detail || (isLink ? "Google link failed." : "Google sign-in failed."));
      }
    })();
  }, [location.hash, navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="card-surface p-8 max-w-md w-full text-center">
        {error ? (
          <>
            <div className="font-display text-2xl text-rose-500 mb-2">Sign-in failed.</div>
            <div className="text-sm text-zinc-400 mb-6">{error}</div>
            <button
              onClick={() => navigate("/login")}
              className="btn-crimson px-6 py-3 rounded-none"
            >
              Back to login
            </button>
          </>
        ) : (
          <>
            <div className="font-display text-2xl mb-2">Signing you in…</div>
            <div className="text-sm text-zinc-500">Opening the vault door.</div>
          </>
        )}
      </div>
    </div>
  );
}
